import React, { useState, useEffect, useMemo } from 'react'
import { 
  IonContent, 
  IonPage, 
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonChip,
  IonGrid,
  IonRow,
  IonCol,
  IonProgressBar,
  IonToast,
  IonSpinner
} from '@ionic/react'
import { 
  analyticsOutline,
  personOutline,
  journalOutline,
  trophyOutline,
  heartOutline,
  sparklesOutline,
  calendarOutline,
  trendingUpOutline,
  checkmarkCircleOutline,
  timeOutline,
  filterOutline,
  searchOutline
} from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import PageHeader from '../../components/PageHeader'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../services/supabase'
import Avatar from '../../components/Avatar'

interface Coachee {
  id: string
  full_name: string
  email: string
  profile_picture_url?: string
  profile_picture_visibility?: string
  // Extended with activity stats
  recent_reflections?: number
  total_reflections?: number
  recent_goals?: number
  total_goals?: number
  last_activity?: string
  avg_mood?: number
}

interface FilterOptions {
  sortBy: 'name' | 'recent_activity' | 'mood' | 'reflections'
  showInactive: boolean
  minReflections: number
}

const CoacheeInsightsPage: React.FC = () => {
  const { user, isCoach } = useAuthStore()
  const { t } = useTranslation()

  // State management
  const [coachees, setCoachees] = useState<Coachee[]>([])
  const [selectedCoacheeId, setSelectedCoacheeId] = useState<string>('')
  const [reflectionEntries, setReflectionEntries] = useState<any[]>([])
  const [goals, setGoals] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [insightsLoading, setInsightsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'reflections' | 'goals'>('overview')
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<FilterOptions>({
    sortBy: 'recent_activity',
    showInactive: true,
    minReflections: 0
  })

  // Reflections tab search and pagination
  const [reflectionsSearch, setReflectionsSearch] = useState('')
  const [reflectionsPage, setReflectionsPage] = useState(1)
  const [reflectionsPerPage, setReflectionsPerPage] = useState(10)

  // Goals tab search and pagination
  const [goalsSearch, setGoalsSearch] = useState('')
  const [goalsPage, setGoalsPage] = useState(1)
  const [goalsPerPage, setGoalsPerPage] = useState(10)

  // Helper function to get mood emoji
  const getMoodEmoji = (mood: number) => {
    if (mood <= 2) return '😢'
    if (mood <= 4) return '😟'
    if (mood <= 6) return '😐'
    if (mood <= 8) return '😊'
    return '😄'
  }

  // Fetch coachees with activity stats
  const fetchCoachees = async () => {
    if (!user || !isCoach()) return

    try {
      console.log('CoacheeInsights: Fetching coachees for coach:', user.id)
      
      const { data, error } = await supabase
        .from('coach_coachee_relationships')
        .select(`
          coachee:profiles!coach_coachee_relationships_coachee_id_fkey (
            id,
            full_name,
            email,
            profile_picture_url,
            profile_picture_visibility
          )
        `)
        .eq('coach_id', user.id)
        .eq('status', 'active')

      console.log('CoacheeInsights: Coachees query result:', { data, error })

      if (error) throw error

      const coacheesList = data?.map((rel: any) => rel.coachee).filter(Boolean) || []
      
      // Fetch activity stats for each coachee
      const coacheesWithStats = await Promise.all(
        coacheesList.map(async (coachee: Coachee) => {
          try {
            // Get reflection stats
            const { data: reflectionStats } = await supabase
              .from('reflection_entries')
              .select('id, mood, date')
              .eq('coachee_id', coachee.id)
              .order('date', { ascending: false })
              .limit(50)

            // Get goal stats
            const { data: goalStats } = await supabase
              .from('goals')
              .select('id, created_at')
              .eq('user_id', coachee.id)
              .order('created_at', { ascending: false })
              .limit(20)

            const now = new Date()
            const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

            const recentReflections = reflectionStats?.filter(r => 
              new Date(r.date) >= thirtyDaysAgo
            ).length || 0

            const recentGoals = goalStats?.filter(g => 
              new Date(g.created_at) >= thirtyDaysAgo
            ).length || 0

            const avgMood = reflectionStats?.length 
              ? Math.round(reflectionStats.reduce((sum, r) => sum + (r.mood || 5), 0) / reflectionStats.length * 10) / 10
              : 0

            const lastActivity = reflectionStats?.[0]?.date || goalStats?.[0]?.created_at

            return {
              ...coachee,
              recent_reflections: recentReflections,
              total_reflections: reflectionStats?.length || 0,
              recent_goals: recentGoals,
              total_goals: goalStats?.length || 0,
              avg_mood: avgMood,
              last_activity: lastActivity
            }
          } catch (error) {
            console.error('Error fetching stats for coachee:', coachee.id, error)
            return coachee
          }
        })
      )
      
      console.log('CoacheeInsights: Processed coachees with stats:', coacheesWithStats)
      
      setCoachees(coacheesWithStats)
    } catch (error) {
      console.error('CoacheeInsights: Error fetching coachees:', error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch coachee insights
  const fetchCoacheeInsights = async (coacheeId: string) => {
    if (!user || !isCoach() || !coacheeId) return

    try {
      setInsightsLoading(true)
      console.log('CoacheeInsights: Fetching insights for coachee:', coacheeId)
      
      const [reflectionsResponse, goalsResponse] = await Promise.all([
        supabase
          .from('reflection_entries')
          .select('*')
          .eq('coachee_id', coacheeId)
          .order('date', { ascending: false })
          .limit(50),
        supabase
          .from('goals')
          .select('*')
          .eq('user_id', coacheeId)
          .order('created_at', { ascending: false })
      ])

      console.log('CoacheeInsights: Reflections response:', reflectionsResponse)
      console.log('CoacheeInsights: Goals response:', goalsResponse)

      if (reflectionsResponse.error) throw reflectionsResponse.error
      if (goalsResponse.error) throw goalsResponse.error

      console.log('CoacheeInsights: Setting reflection entries:', reflectionsResponse.data?.length || 0, 'entries')
      console.log('CoacheeInsights: Setting goals:', goalsResponse.data?.length || 0, 'goals')

      setReflectionEntries(reflectionsResponse.data || [])
      setGoals(goalsResponse.data || [])
    } catch (error) {
      console.error('CoacheeInsights: Error fetching coachee insights:', error)
      setToastMessage('Failed to load coachee insights')
      setShowToast(true)
    } finally {
      setInsightsLoading(false)
    }
  }

  // Filter and sort coachees
  const filteredAndSortedCoachees = useMemo(() => {
    const filtered = coachees.filter(coachee => {
      // Search filter
      const searchMatch = !searchQuery || 
        coachee.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        coachee.email?.toLowerCase().includes(searchQuery.toLowerCase())

      // Activity filter
      const hasActivity = (coachee.total_reflections || 0) >= filters.minReflections
      const isActive = coachee.last_activity && 
        new Date(coachee.last_activity) > new Date(Date.now() - 60 * 24 * 60 * 60 * 1000) // 60 days

      const activityMatch = filters.showInactive || hasActivity || isActive

      return searchMatch && activityMatch
    })

    // Sort
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'name':
          return (a.full_name || a.email).localeCompare(b.full_name || b.email)
        case 'recent_activity': {
          const aActivity = a.last_activity ? new Date(a.last_activity).getTime() : 0
          const bActivity = b.last_activity ? new Date(b.last_activity).getTime() : 0
          return bActivity - aActivity
        }
        case 'mood':
          return (b.avg_mood || 0) - (a.avg_mood || 0)
        case 'reflections':
          return (b.recent_reflections || 0) - (a.recent_reflections || 0)
        default:
          return 0
      }
    })

    return filtered
  }, [coachees, searchQuery, filters])

  // Filter and paginate reflections
  const filteredReflections = useMemo(() => {
    const filtered = reflectionEntries.filter(reflection => {
      if (!reflectionsSearch) return true
      return (
        reflection.thoughts?.toLowerCase().includes(reflectionsSearch.toLowerCase()) ||
        reflection.insights?.toLowerCase().includes(reflectionsSearch.toLowerCase()) ||
        reflection.challenges?.toLowerCase().includes(reflectionsSearch.toLowerCase()) ||
        reflection.tags?.some((tag: string) => tag.toLowerCase().includes(reflectionsSearch.toLowerCase()))
      )
    })
    return filtered
  }, [reflectionEntries, reflectionsSearch])

  const paginatedReflections = useMemo(() => {
    const startIndex = (reflectionsPage - 1) * reflectionsPerPage
    return filteredReflections.slice(startIndex, startIndex + reflectionsPerPage)
  }, [filteredReflections, reflectionsPage, reflectionsPerPage])

  const reflectionsTotalPages = Math.ceil(filteredReflections.length / reflectionsPerPage)

  // Filter and paginate goals
  const filteredGoals = useMemo(() => {
    const filtered = goals.filter(goal => {
      if (!goalsSearch) return true
      return (
        goal.title?.toLowerCase().includes(goalsSearch.toLowerCase()) ||
        goal.description?.toLowerCase().includes(goalsSearch.toLowerCase()) ||
        goal.category?.toLowerCase().includes(goalsSearch.toLowerCase())
      )
    })
    return filtered
  }, [goals, goalsSearch])

  const paginatedGoals = useMemo(() => {
    const startIndex = (goalsPage - 1) * goalsPerPage
    return filteredGoals.slice(startIndex, startIndex + goalsPerPage)
  }, [filteredGoals, goalsPage, goalsPerPage])

  const goalsTotalPages = Math.ceil(filteredGoals.length / goalsPerPage)

  useEffect(() => {
    fetchCoachees()
  }, [user])

  useEffect(() => {
    if (selectedCoacheeId) {
      fetchCoacheeInsights(selectedCoacheeId)
    }
  }, [selectedCoacheeId])

  if (!user || !isCoach()) {
    return (
      <IonPage>
        <PageHeader title={t('sessions.coacheeInsights')} />
        <IonContent className="ion-padding">
          <div className="text-center py-12">
            <IonIcon icon={personOutline} className="text-6xl text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('sessions.accessDenied')}</h3>
            <p className="text-gray-500">{t('sessions.coachAccessRequired')}</p>
          </div>
        </IonContent>
      </IonPage>
    )
  }

  return (
    <IonPage>
      <PageHeader title={t('sessions.coacheeInsights')} />
      <IonContent>
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <IonSpinner name="crescent" />
          </div>
        ) : (
          <>
            {/* Coachee Selection */}
            <IonCard className="mx-4 mt-4">
              <IonCardHeader>
                <IonCardTitle className="text-lg">{t('sessions.selectCoachee')}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {/* Search and Filters */}
                <div className="mb-4 space-y-4">
                  <IonSearchbar
                    value={searchQuery}
                    onIonInput={(e) => setSearchQuery(e.detail.value!)}
                    placeholder={t('sessions.searchCoachees')}
                    showClearButton="focus"
                    debounce={300}
                  />

                  <div className="flex items-center justify-between">
                    <IonButton
                      fill="outline"
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <IonIcon icon={filterOutline} slot="start" />
                      {t('sessions.filters')}
                    </IonButton>
                    <span className="text-sm text-gray-600">
                      {filteredAndSortedCoachees.length} {t('sessions.of')} {coachees.length} {t('sessions.coachees')}
                    </span>
                  </div>

                  {/* Filter Options */}
                  {showFilters && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <IonLabel className="text-sm font-medium">{t('sessions.sortBy')}</IonLabel>
                        <IonSelect
                          value={filters.sortBy}
                          onIonChange={(e) => setFilters({...filters, sortBy: e.detail.value})}
                        >
                          <IonSelectOption value="recent_activity">{t('sessions.recentActivity')}</IonSelectOption>
                          <IonSelectOption value="name">{t('sessions.name')}</IonSelectOption>
                          <IonSelectOption value="mood">{t('sessions.avgMood')}</IonSelectOption>
                          <IonSelectOption value="reflections">{t('sessions.reflections')}</IonSelectOption>
                        </IonSelect>
                      </div>
                      <div>
                        <IonLabel className="text-sm font-medium">{t('sessions.minReflections')}</IonLabel>
                        <IonSelect
                          value={filters.minReflections}
                          onIonChange={(e) => setFilters({...filters, minReflections: e.detail.value})}
                        >
                          <IonSelectOption value={0}>0+</IonSelectOption>
                          <IonSelectOption value={1}>1+</IonSelectOption>
                          <IonSelectOption value={5}>5+</IonSelectOption>
                          <IonSelectOption value={10}>10+</IonSelectOption>
                        </IonSelect>
                      </div>
                    </div>
                  )}
                </div>

                {/* Coachee List */}
                {filteredAndSortedCoachees.length > 0 ? (
                  <div className="space-y-3">
                    {filteredAndSortedCoachees.map((coachee) => (
                      <div
                        key={coachee.id}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedCoacheeId === coachee.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedCoacheeId(coachee.id)}
                      >
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0">
                            <Avatar 
                              user={{
                                id: coachee.id,
                                full_name: coachee.full_name,
                                email: coachee.email,
                                profile_picture_url: coachee.profile_picture_url,
                                profile_picture_visibility: coachee.profile_picture_visibility as any
                              }}
                              currentUserId={user?.id}
                              relationship="coach"
                              size="medium"
                              className="w-12 h-12"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate">
                              {coachee.full_name || coachee.email}
                            </h4>
                            <p className="text-sm text-gray-500 truncate">{coachee.email}</p>
                            
                            {/* Activity Stats */}
                            <div className="flex flex-wrap gap-2 mt-2">
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded whitespace-nowrap">
                                {coachee.total_reflections || 0} {t('sessions.totalReflections')}
                              </span>
                              {coachee.avg_mood && (
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded whitespace-nowrap">
                                  {getMoodEmoji(coachee.avg_mood)} {coachee.avg_mood}
                                </span>
                              )}
                              {(coachee.recent_reflections || 0) > 0 && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded whitespace-nowrap">
                                  {coachee.recent_reflections} {t('sessions.recentReflections')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <IonIcon icon={searchOutline} className="text-6xl text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('sessions.noMatchingCoachees')}</h3>
                    <p className="text-gray-500">{t('sessions.adjustFilters')}</p>
                  </div>
                )}
              </IonCardContent>
            </IonCard>

            {/* Insights Content */}
            {selectedCoacheeId && (
              <>
                {insightsLoading ? (
                  <div className="flex justify-center items-center h-32 mx-4">
                    <IonSpinner name="crescent" />
                  </div>
                ) : (
                  <div className="mx-4 my-4">
                    {/* Tab Navigation */}
                    <IonSegment 
                      value={activeTab} 
                      onIonChange={(e) => setActiveTab(e.detail.value as any)}
                      className="mb-4"
                    >
                      <IonSegmentButton value="overview">
                        <IonIcon icon={analyticsOutline} />
                        <IonLabel>{t('sessions.tabs.overview')}</IonLabel>
                      </IonSegmentButton>
                      <IonSegmentButton value="reflections">
                        <IonIcon icon={journalOutline} />
                        <IonLabel>{t('sessions.tabs.reflections')}</IonLabel>
                      </IonSegmentButton>
                      <IonSegmentButton value="goals">
                        <IonIcon icon={trophyOutline} />
                        <IonLabel>{t('sessions.tabs.goals')}</IonLabel>
                      </IonSegmentButton>
                    </IonSegment>

                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                      <div className="space-y-4">
                        <IonGrid>
                          <IonRow>
                            <IonCol size="12" sizeMd="4">
                              <IonCard>
                                <IonCardContent className="text-center">
                                  <IonIcon icon={journalOutline} className="text-4xl text-blue-500 mb-2" />
                                  <h3 className="text-2xl font-bold">{reflectionEntries.length}</h3>
                                  <p className="text-gray-600">{t('sessions.overview.totalReflections')}</p>
                                </IonCardContent>
                              </IonCard>
                            </IonCol>
                            <IonCol size="12" sizeMd="4">
                              <IonCard>
                                <IonCardContent className="text-center">
                                  <IonIcon icon={trophyOutline} className="text-4xl text-green-500 mb-2" />
                                  <h3 className="text-2xl font-bold">{goals.length}</h3>
                                  <p className="text-gray-600">{t('sessions.overview.totalGoals')}</p>
                                </IonCardContent>
                              </IonCard>
                            </IonCol>
                            <IonCol size="12" sizeMd="4">
                              <IonCard>
                                <IonCardContent className="text-center">
                                  <IonIcon icon={heartOutline} className="text-4xl text-red-500 mb-2" />
                                  <h3 className="text-2xl font-bold">
                                    {reflectionEntries.length > 0
                                      ? Math.round(reflectionEntries.reduce((sum, r) => sum + (r.mood || 5), 0) / reflectionEntries.length * 10) / 10
                                      : '—'
                                    }
                                  </h3>
                                  <p className="text-gray-600">{t('sessions.overview.avgMood')}</p>
                                </IonCardContent>
                              </IonCard>
                            </IonCol>
                          </IonRow>
                        </IonGrid>

                        {/* Recent Reflections Preview */}
                        <IonCard>
                          <IonCardHeader>
                            <IonCardTitle className="flex items-center text-base">
                              <IonIcon icon={journalOutline} className="mr-2" />
                              {t('sessions.overview.recentReflections')}
                            </IonCardTitle>
                          </IonCardHeader>
                          <IonCardContent>
                            {reflectionEntries.length > 0 ? (
                              <div className="space-y-3">
                                {reflectionEntries.slice(0, 3).map((reflection) => (
                                  <div key={reflection.id} className="border-l-4 border-blue-200 pl-3 py-2">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm font-medium">
                                        {new Date(reflection.date).toLocaleDateString()}
                                      </span>
                                      <span className="text-xl">{getMoodEmoji(reflection.mood || 5)}</span>
                                    </div>
                                    {reflection.thoughts && (
                                      <p className="text-sm text-gray-700 line-clamp-2">
                                        {reflection.thoughts}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-center text-gray-500 py-4">{t('sessions.overview.noReflectionsYet')}</p>
                            )}
                          </IonCardContent>
                        </IonCard>
                      </div>
                    )}

                    {/* Reflections Tab */}
                    {activeTab === 'reflections' && (
                      <IonCard>
                        <IonCardHeader>
                          <IonCardTitle className="flex items-center text-base">
                            <IonIcon icon={journalOutline} className="mr-2" />
                            {t('sessions.reflectionsTab.reflectionEntries')}
                          </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                          {/* Search */}
                          <div className="mb-4">
                            <IonSearchbar
                              value={reflectionsSearch}
                              onIonInput={(e) => {
                                setReflectionsSearch(e.detail.value!)
                                setReflectionsPage(1) // Reset to first page when searching
                              }}
                              placeholder={t('sessions.reflectionsTab.searchPlaceholder')}
                              showClearButton="focus"
                              debounce={300}
                            />
                          </div>

                          {filteredReflections.length > 0 ? (
                            <>
                              <div className="space-y-4">
                                {paginatedReflections.map((reflection) => (
                                  <div key={reflection.id} className="border-l-4 border-blue-200 pl-4 py-3 bg-gray-50 rounded-r-lg">
                                    <div className="flex items-center justify-between mb-3">
                                      <span className="text-sm font-medium">
                                        {new Date(reflection.date).toLocaleDateString('en-US', { 
                                          weekday: 'short', 
                                          month: 'short', 
                                          day: 'numeric' 
                                        })}
                                      </span>
                                      <div className="flex items-center">
                                        <span className="text-xl mr-1">{getMoodEmoji(reflection.mood || 5)}</span>
                                        <IonChip color="primary" className="text-xs">
                                          {reflection.mood}/10
                                        </IonChip>
                                      </div>
                                    </div>
                                    
                                    {reflection.thoughts && (
                                      <div className="mb-3">
                                        <h4 className="text-sm font-medium text-gray-800 mb-1">{t('sessions.reflectionsTab.thoughts')}</h4>
                                        <p className="text-sm text-gray-700">{reflection.thoughts}</p>
                                      </div>
                                    )}
                                    
                                    {reflection.insights && (
                                      <div className="mb-3">
                                        <h4 className="text-sm font-medium text-gray-800 mb-1">{t('sessions.reflectionsTab.insights')}</h4>
                                        <p className="text-sm text-gray-600">{reflection.insights}</p>
                                      </div>
                                    )}
                                    
                                    {reflection.challenges && (
                                      <div className="mb-3">
                                        <h4 className="text-sm font-medium text-gray-800 mb-1">{t('sessions.reflectionsTab.challenges')}</h4>
                                        <p className="text-sm text-gray-600">{reflection.challenges}</p>
                                      </div>
                                    )}
                                    
                                    {reflection.tags && reflection.tags.length > 0 && (
                                      <div className="flex flex-wrap gap-1">
                                        {reflection.tags.map((tag: string, index: number) => (
                                          <IonChip key={index} className="text-xs" color="primary">
                                            {tag}
                                          </IonChip>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>

                              {/* Pagination for Reflections */}
                              {reflectionsTotalPages > 1 && (
                                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm text-gray-600">
                                      {t('sessions.pagination.show')}
                                    </span>
                                    <IonSelect
                                      value={reflectionsPerPage}
                                      onIonChange={(e) => {
                                        setReflectionsPerPage(e.detail.value)
                                        setReflectionsPage(1)
                                      }}
                                      className="w-20"
                                    >
                                      <IonSelectOption value={5}>5</IonSelectOption>
                                      <IonSelectOption value={10}>10</IonSelectOption>
                                      <IonSelectOption value={20}>20</IonSelectOption>
                                    </IonSelect>
                                  </div>

                                  <div className="flex items-center space-x-2">
                                    <IonButton
                                      fill="clear"
                                      size="small"
                                      disabled={reflectionsPage === 1}
                                      onClick={() => setReflectionsPage(reflectionsPage - 1)}
                                    >
                                      {t('sessions.pagination.previous')}
                                    </IonButton>
                                    
                                    <span className="text-sm text-gray-600 px-3">
                                      {t('sessions.pagination.page')} {reflectionsPage} {t('sessions.pagination.of')} {reflectionsTotalPages}
                                    </span>
                                    
                                    <IonButton
                                      fill="clear"
                                      size="small"
                                      disabled={reflectionsPage === reflectionsTotalPages}
                                      onClick={() => setReflectionsPage(reflectionsPage + 1)}
                                    >
                                      {t('sessions.pagination.next')}
                                    </IonButton>
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-center py-8">
                              <IonIcon icon={journalOutline} className="text-6xl text-gray-400 mb-4" />
                              <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {reflectionsSearch ? t('sessions.reflectionsTab.noMatchingReflections') : t('sessions.reflectionsTab.noReflectionsYet')}
                              </h3>
                              <p className="text-gray-500">
                                {reflectionsSearch ? '' : t('sessions.reflectionsTab.noReflectionsMessage')}
                              </p>
                            </div>
                          )}
                        </IonCardContent>
                      </IonCard>
                    )}

                    {/* Goals Tab */}
                    {activeTab === 'goals' && (
                      <IonCard>
                        <IonCardHeader>
                          <IonCardTitle className="flex items-center text-base">
                            <IonIcon icon={trophyOutline} className="mr-2" />
                            {t('sessions.goalsTab.goals')}
                          </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                          {/* Search */}
                          <div className="mb-4">
                            <IonSearchbar
                              value={goalsSearch}
                              onIonInput={(e) => {
                                setGoalsSearch(e.detail.value!)
                                setGoalsPage(1) // Reset to first page when searching
                              }}
                              placeholder={t('sessions.goalsTab.searchPlaceholder')}
                              showClearButton="focus"
                              debounce={300}
                            />
                          </div>

                          {filteredGoals.length > 0 ? (
                            <>
                              <div className="space-y-4">
                                {paginatedGoals.map((goal) => (
                                  <div key={goal.id} className="border rounded-lg p-4 bg-gray-50">
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-gray-900 mb-1">{goal.title}</h4>
                                        {goal.description && (
                                          <p className="text-sm text-gray-600 mb-2">{goal.description}</p>
                                        )}
                                      </div>
                                      <IonChip 
                                        color={
                                          goal.status === 'completed' ? 'success' :
                                          goal.status === 'paused' ? 'warning' : 'primary'
                                        }
                                        className="text-xs"
                                      >
                                        {goal.status}
                                      </IonChip>
                                    </div>

                                    <div className="mb-3">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm font-medium">{t('sessions.goalsTab.progress')}</span>
                                        <span className="text-sm text-gray-600">{goal.progress}%</span>
                                      </div>
                                      <IonProgressBar 
                                        value={goal.progress / 100} 
                                        color={goal.progress === 100 ? 'success' : 'primary'}
                                      />
                                    </div>

                                    <div className="flex items-center justify-between text-sm text-gray-500">
                                      {goal.category && (
                                        <span className="bg-gray-200 px-2 py-1 rounded text-xs">
                                          {goal.category}
                                        </span>
                                      )}
                                      {goal.target_date && (
                                        <span className="text-xs">
                                          {t('sessions.goalsTab.due')}: {new Date(goal.target_date).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Pagination for Goals */}
                              {goalsTotalPages > 1 && (
                                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm text-gray-600">
                                      {t('sessions.pagination.show')}
                                    </span>
                                    <IonSelect
                                      value={goalsPerPage}
                                      onIonChange={(e) => {
                                        setGoalsPerPage(e.detail.value)
                                        setGoalsPage(1)
                                      }}
                                      className="w-20"
                                    >
                                      <IonSelectOption value={5}>5</IonSelectOption>
                                      <IonSelectOption value={10}>10</IonSelectOption>
                                      <IonSelectOption value={20}>20</IonSelectOption>
                                    </IonSelect>
                                  </div>

                                  <div className="flex items-center space-x-2">
                                    <IonButton
                                      fill="clear"
                                      size="small"
                                      disabled={goalsPage === 1}
                                      onClick={() => setGoalsPage(goalsPage - 1)}
                                    >
                                      {t('sessions.pagination.previous')}
                                    </IonButton>
                                    
                                    <span className="text-sm text-gray-600 px-3">
                                      {t('sessions.pagination.page')} {goalsPage} {t('sessions.pagination.of')} {goalsTotalPages}
                                    </span>
                                    
                                    <IonButton
                                      fill="clear"
                                      size="small"
                                      disabled={goalsPage === goalsTotalPages}
                                      onClick={() => setGoalsPage(goalsPage + 1)}
                                    >
                                      {t('sessions.pagination.next')}
                                    </IonButton>
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-center py-8">
                              <IonIcon icon={trophyOutline} className="text-6xl text-gray-400 mb-4" />
                              <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {goalsSearch ? t('sessions.goalsTab.noMatchingGoals') : t('sessions.goalsTab.noGoalsYet')}
                              </h3>
                              <p className="text-gray-500">
                                {goalsSearch ? '' : t('sessions.goalsTab.noGoalsMessage')}
                              </p>
                            </div>
                          )}
                        </IonCardContent>
                      </IonCard>
                    )}
                  </div>
                )}
              </>
            )}

            {!selectedCoacheeId && coachees.length === 0 && !loading && (
              <div className="text-center py-12 px-4">
                <IonIcon icon={personOutline} className="text-6xl text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('sessions.noCoachees.title')}</h3>
                <p className="text-gray-500 mb-6">
                  {t('sessions.noCoachees.message')}
                </p>
              </div>
            )}
          </>
        )}

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="bottom"
        />
      </IonContent>
    </IonPage>
  )
}

export default CoacheeInsightsPage 